import React, { useEffect, useState } from 'react'
import HomeTemplate from '../templates/HomeTemplate'
import FAQAccordion from '../components/FAQAccordion'
import useCanonicalUrl from '../hooks/useCanonicalUrl'
import { Mail, Send, Globe, MessageCircle, Clock, CheckCircle } from 'lucide-react'
import './Contact.css'

const CONTACT_EMAIL = 'admin@everykits.com';

const InstagramIcon = ({ size = 24, ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...rest}>
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
  </svg>
);

const FacebookIcon = ({ size = 24, ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" {...rest}>
    <path d="M22 12a10 10 0 1 0-11.6 9.87v-6.99H7.9V12h2.5V9.8c0-2.5 1.49-3.89 3.77-3.89 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.78l-.44 2.88h-2.34v6.99A10 10 0 0 0 22 12z" />
  </svg>
);

const LinkedinIcon = ({ size = 24, ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" {...rest}>
    <path d="M19 3A2 2 0 0 1 21 5v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14zM8.34 18v-7.7H5.67V18h2.67zM7 9.24A1.54 1.54 0 1 0 7 6.16a1.54 1.54 0 0 0 0 3.08zM18.34 18v-4.24c0-2.27-1.21-3.32-2.83-3.32a2.44 2.44 0 0 0-2.21 1.22h-.03v-1.05h-2.56c.03.7 0 7.39 0 7.39h2.56v-4.13c0-.22.02-.44.08-.6.18-.44.6-.9 1.3-.9.92 0 1.29.7 1.29 1.72V18h2.4z" />
  </svg>
);

const TiktokIcon = ({ size = 24, ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" {...rest}>
    <path d="M16.5 3c.3 2.1 1.6 3.7 3.7 4v2.6c-1.4 0-2.7-.4-3.7-1.2v6.2c0 3.3-2.7 6-6 6s-6-2.7-6-6 2.7-6 6-6c.3 0 .6 0 .9.1v2.7c-.3-.1-.6-.2-.9-.2-1.9 0-3.4 1.5-3.4 3.4S8.6 18 10.5 18s3.4-1.5 3.4-3.4V3h2.6z" />
  </svg>
);

const SOCIAL_LINKS = [
  { id: 'instagram', name: 'Instagram', handle: '@traindevs', url: 'https://instagram.com/traindevs', icon: InstagramIcon },
  { id: 'facebook', name: 'Facebook', handle: 'TrainDevs', url: 'https://facebook.com/traindevs', icon: FacebookIcon },
  { id: 'linkedin', name: 'LinkedIn', handle: 'TrainDevs', url: 'https://linkedin.com/company/traindevs', icon: LinkedinIcon },
  { id: 'tiktok', name: 'TikTok', handle: '@traindevs', url: 'https://tiktok.com/@traindevs', icon: TiktokIcon },
];

const Contact = () => {
  useCanonicalUrl();
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  useEffect(() => {
    document.title = 'Contact Us - EveryKits';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Get in touch with the EveryKits team for support, feedback, or partnership inquiries about our free online tools.');
    }
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(formData.subject);
    const body = encodeURIComponent(
      `Name: ${formData.name}\nEmail: ${formData.email}\n\nMessage:\n${formData.message}`
    );
    const mailtoLink = `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`;
    window.location.href = mailtoLink;
    setSubmitMessage(`Opening your email client... If it doesn't open, email us directly at ${CONTACT_EMAIL}`);
    setTimeout(() => {
      setFormData({ name: '', email: '', subject: '', message: '' });
      setSubmitMessage('');
    }, 3000);
  };

  return (
    <HomeTemplate>
      <div className="contact">
        <div className="contact-hero">
          <div className="contact-methods">
            <a href={`mailto:${CONTACT_EMAIL}`} className="contact-card">
              <div className="contact-icon"><Mail size={24} /></div>
              <h3>Email Us</h3>
              <p>{CONTACT_EMAIL}</p>
              <span className="contact-badge">Best way to reach us</span>
            </a>

            <a href="/" className="contact-card">
              <div className="contact-icon"><Globe size={24} /></div>
              <h3>Browse Tools</h3>
              <p>everykits.com</p>
              <span className="contact-badge">All free, no signup</span>
            </a>

            <a href={`mailto:${CONTACT_EMAIL}?subject=Feedback`} className="contact-card">
              <div className="contact-icon"><MessageCircle size={24} /></div>
              <h3>Feedback & Ideas</h3>
              <p>Tell us what to build</p>
              <span className="contact-badge">We reply personally</span>
            </a>
          </div>

          <div className="social-section">
            <div className="social-header">
              <h2>Follow TrainDevs</h2>
              <p>EveryKits is built by TrainDevs. Follow us for updates, dev logs, and new tool launches.</p>
            </div>
            <div className="social-links">
              {SOCIAL_LINKS.map((social) => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.id}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-card"
                    aria-label={`Follow TrainDevs on ${social.name}`}
                  >
                    <div className={`social-icon social-icon-${social.id}`}>
                      <Icon size={24} />
                    </div>
                    <h3>{social.name}</h3>
                    <p>{social.handle}</p>
                  </a>
                );
              })}
            </div>
          </div>

          <div className="contact-form-section">
            <div className="contact-form-wrapper">
              <div className="form-header">
                <h2>Send us a Message</h2>
                <p>Fill this out and it'll open your email app with everything ready to send.</p>
              </div>

              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Your Name</label>
                    <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required placeholder="John Doe" />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">Email Address</label>
                    <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required placeholder="john@example.com" />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="subject">Subject</label>
                  <input type="text" id="subject" name="subject" value={formData.subject} onChange={handleChange} required placeholder="How can we help you?" />
                </div>

                <div className="form-group">
                  <label htmlFor="message">Message</label>
                  <textarea id="message" name="message" value={formData.message} onChange={handleChange} required rows={5} placeholder="Tell us more about your inquiry..." />
                </div>

                <button type="submit" className="submit-btn" disabled={isSubmitting}>
                  {isSubmitting ? (<><div className="spinner"></div>Sending...</>) : (<><Send size={18} />Send Message</>)}
                </button>

                {submitMessage && (
                  <div className="success-message">
                    <CheckCircle size={20} />
                    {submitMessage}
                  </div>
                )}
              </form>
            </div>
          </div>

          <div className="faq-section">
            <FAQAccordion />
          </div>

          <div className="response-section">
            <div className="response-header">
              <Clock size={32} />
              <h2>When to expect a reply</h2>
            </div>
            <p style={{ textAlign: 'center', color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
              We're a small team, so replies usually take a day or two — sometimes sooner. Every message reaches a real person, and we genuinely read all of them.
            </p>
          </div>
        </div>
      </div>
    </HomeTemplate>
  )
}

export default Contact